import _ from 'underscore';

import {
  RECOMMENDATION_TYPES,
} from '@bingads-webui-campaign/recommendation-core';

export const adTypeMapping = {
  [RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS]: 'DynamicSearch',
  [RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS]: 'ResponsiveSearch',
  [RECOMMENDATION_TYPES.MULTIMEDIA_ADS]: 'Responsive',
  [RECOMMENDATION_TYPES.IMPROVE_RESPONSIVE_SEARCH_ADS]: 'ResponsiveSearch',
  [RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS]: 'Responsive',
};

const responsiveSearchFields = [
  'FinalUrls',
  'MobileFinalUrls',
  'Path1',
  'Path2',
  'Headlines',
  'Descriptions',
  'TrackingUrlTemplate',
  'NativePreference',
  'FinalUrlSuffix',
  'UrlCustomParameters',
];

const multimediaFields = [
  'BusinessName',
  'FinalUrls',
  'MobileFinalUrls',
  'Headlines',
  'LongHeadlines',
  'Descriptions',
  'TrackingUrlTemplate',
  'NativePreference',
  'FinalUrlSuffix',
  'UrlCustomParameters',
  'CallToAction',
  'CallToActionLanguage',
  'Images',
];

const improveResponsiveSearchFields = [
  'Headlines',
  'Descriptions',
];

const improveMultimediaFields = [
  'Headlines',
  'LongHeadlines',
  'Descriptions',
  'Images',
];

export const adTypeFieldsMapping = {
  [RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS]: responsiveSearchFields,
  [RECOMMENDATION_TYPES.MULTIMEDIA_ADS]: multimediaFields,
  [RECOMMENDATION_TYPES.IMPROVE_RESPONSIVE_SEARCH_ADS]: improveResponsiveSearchFields,
  [RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS]: improveMultimediaFields,
};

export const descriptionHeaderMapping = {
  [RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS]:
    'Recommendation_Inline_View_Responsive_Search_Ads_Description_Header',
  [RECOMMENDATION_TYPES.MULTIMEDIA_ADS]:
    'Recommendation_Inline_View_Multimedia_Ads_Description_Header',
  [RECOMMENDATION_TYPES.IMPROVE_RESPONSIVE_SEARCH_ADS]:
    'Recommendation_Inline_View_Improve_Responsive_Search_Ads_Description_Header',
  [RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS]:
    'Recommendation_Inline_View_Improve_Multimedia_Ads_Description_Header',
};

// Transform data for AdInsight MT apply with user input format
export const transformResponsiveSearchAd = (model) => {
  const applyModel = _.clone(model);
  applyModel.Headlines = _.map(applyModel.Headlines, headline => ({
    PinnedFieldTypeString: headline.PinnedField,
    Text: headline.Asset.Text,
  }));
  applyModel.Descriptions = _.map(applyModel.Descriptions, description => ({
    PinnedFieldTypeString: description.PinnedField,
    Text: description.Asset.Text,
  }));
  return applyModel;
};

export const transformImproveResponsiveSearchAd = (model) => {
  const applyModel = _.clone(model);
  applyModel.Headlines = _.map(applyModel.Headlines, headline => ({
    Text: headline.Asset.Text,
    Source: headline.IsAIGeneratedAsset ? 'AlgoGenerated_Headline' : undefined,
  }));
  applyModel.Descriptions = _.map(applyModel.Descriptions, description => ({
    Text: description.Asset.Text,
    Source: description.IsAIGeneratedAsset ? 'AlgoGenerated_Description' : undefined,
  }));
  return applyModel;
};

const isImageAssetId = (str) => {
  if (typeof str === 'number') return true;
  return typeof str === 'string' && !_.isNaN(Number(str));
};

const getAssociationMetadata = (image, isRecommendedImage) => {
  if (isRecommendedImage) {
    return {
      SubType: image.subType,
      CropX: null,
      CropY: null,
      CropWidth: null,
      CropHeight: null,
    };
  }
  return {
    SubType: image.subType,
    CropX: image.cropX,
    CropY: image.cropY,
    CropWidth: image.cropWidth,
    CropHeight: image.cropHeight,
  };
};

// Transform data for AdInsight MT apply with user input format
export const transformMultimediaAd = (
  model,
  recommendedImageAssetIdStrings,
  imageFeatureMaskMap
) => {
  const applyModel = _.omit(
    model,
    'Headlines',
    'LongHeadlines',
    'Descriptions',
    'Images',
    'TrackingUrlTemplate',
    'FinalUrlSuffix',
    'UrlCustomParameters'
  );

  // Short headline, long headline and ad text
  applyModel.ShortHeadlines = _.map(JSON.parse(model.Headlines), headline => ({
    Text: headline.Text,
  }));
  applyModel.LongHeadlines = _.map(JSON.parse(model.LongHeadlines), longHeadline => ({
    Text: longHeadline.Text,
  }));
  applyModel.AdTexts = _.map(JSON.parse(model.Descriptions), description => ({
    Text: description.Text,
  }));

  // Aggregate images to 2 layers like the original data structure from response payload
  if (!_.isUndefined(model.Images) && _.isString(model.Images)) {
    const imagesMap = {};
    _.each(JSON.parse(model.Images), (image) => {
      const key = image.idString || image.url;
      const isRecommendedImage = _.contains(recommendedImageAssetIdStrings, image.idString)
        || !isImageAssetId(image.idString);
      if (key in imagesMap) {
        const existingImage = imagesMap[key];
        existingImage.AssociationMetadata.push(getAssociationMetadata(image, isRecommendedImage));
      } else {
        imagesMap[key] = {
          ImageAssetIdString: isImageAssetId(image.idString) ? image.idString : undefined,
          ImageUrl: isImageAssetId(image.idString) ? undefined : image.idString,
          AssociationMetadata: [getAssociationMetadata(image, isRecommendedImage)],
          FeatureMask: isRecommendedImage && !_.isUndefined(imageFeatureMaskMap) ?
            imageFeatureMaskMap.get(key) : undefined,
        };
      }
    });
    const images = [];
    _.each(imagesMap, image => images.push(image));
    applyModel.Images = images;
  }

  // TrackingUrlTemplate, FinalUrlSuffix and UrlCustomParameters
  applyModel.TrackingUrlTemplate = model.TrackingUrlTemplate !== 'delete_value' ?
    model.TrackingUrlTemplate : undefined;
  applyModel.FinalUrlSuffix = model.FinalUrlSuffix !== 'delete_value' ?
    model.FinalUrlSuffix : undefined;
  applyModel.UrlCustomParameters = model.UrlCustomParameters !== 'delete_value' ?
    model.UrlCustomParameters : undefined;

  return applyModel;
};

export const transformImproveMultimediaAd = (model) => {
  const applyModel = {};

  // Short headline, long headline and ad text
  applyModel.Headlines = _.map(JSON.parse(model.Headlines), headline => ({
    Text: headline.Text,
  }));
  applyModel.LongHeadlines = _.map(JSON.parse(model.LongHeadlines), longHeadline => ({
    Text: longHeadline.Text,
  }));
  applyModel.Descriptions = _.map(JSON.parse(model.Descriptions), description => ({
    Text: description.Text,
  }));

  // Aggregate images to 2 layers like the original data structure from response payload
  if (!_.isUndefined(model.Images) && _.isString(model.Images)) {
    const imagesMap = {};
    _.each(JSON.parse(model.Images), (image) => {
      const key = image.idString || image.url;
      if (key in imagesMap) {
        const existingImage = imagesMap[key];
        existingImage.AssociationMetadata.push({
          SubType: image.subType,
          CropX: image.cropX,
          CropY: image.cropY,
          CropWidth: image.cropWidth,
          CropHeight: image.cropHeight,
        });
      } else {
        imagesMap[key] = {
          AssetIdString: isImageAssetId(image.idString) ? image.idString : undefined,
          ImageURL: isImageAssetId(image.idString) ? undefined : image.idString,
          AssociationMetadata: [{
            SubType: image.subType,
            CropX: image.cropX,
            CropY: image.cropY,
            CropWidth: image.cropWidth,
            CropHeight: image.cropHeight,
          }],
        };
      }
    });
    const images = [];
    _.each(imagesMap, image => images.push(image));
    applyModel.Images = images;
  }

  return applyModel;
};
